/***********************************************************************************
 * Path cleaning for security track history.
 *
 * Motion-gated path:
 *  - Prefer drawing movement only when tilt/vibration indicates physical
 *    disturbance, plus sustained displacement.
 *  - GPSPRO "MOVING" and GPS speed are weak signals (they fire on GPS drift) and
 *    alone are not enough to leave a stop.
 * Stop clustering (tilt-quiet):
 *  - While quiet, collapse GPS spiderwebs into a WiFi-anchored stop (or GPS
 *    centroid when no WiFi fix is available).
 *
 * Works without motions[] (displacement fallback). With tilt motions from
 * fngetsecuritytrackhistory the gate is much more reliable.
 ***********************************************************************************/

// Stationary GPS multipath in urban areas routinely jumps 50–150m; keep the
// stop sticky until something clearly leaves that envelope.
const STOP_RADIUS_M = 80;
const EXIT_MOVE_M = 120;
const EXIT_CONFIRM_POINTS = 2; // consecutive leave candidates before exiting stop
const MIN_VERTEX_M = 40;
const SPEED_MOTION_KMH = 10; // weak signal; GPS often reports 3–8 while parked
const HARD_EXIT_M = 250; // huge jump + speed can exit even without tilt
const MOTION_HOLD_MS = 2 * 60 * 1000; // tilt pulse window (shorter = less drift bleed)
const DISPLAY_STOP_RADIUS_M = 35;

function toRad(d) {
    return d * Math.PI / 180;
}

export function haversineMeters(a, b) {
    const R = 6371000;
    const dLat = toRad(b.latitude - a.latitude);
    const dLng = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function pointTimeMs(p) {
    if (p._ms != null) return p._ms;
    const d = (typeof DplLink !== 'undefined' && DplLink.dateFromPostgress)
        ? DplLink.dateFromPostgress(p.eventtime)
        : new Date(p.eventtime);
    return d ? d.getTime() : 0;
}

function isWifi(p) {
    return Number(p.direction) === 11;
}

/**
 * Build look-ups for strong (tilt) vs weak (GPSPRO moving) motion.
 * MOVING is GPS-derived and often true during stationary drift, so it must not
 * be enough on its own to leave a stop.
 */
function buildMotionFns(motions) {
    const events = (motions || []).map(m => {
        const d = (typeof DplLink !== 'undefined' && DplLink.dateFromPostgress)
            ? DplLink.dateFromPostgress(m.eventtime)
            : new Date(m.eventtime);
        return {
            ms: d ? d.getTime() : 0,
            type: m.type,
            state: Number(m.state) === 0 ? 0 : 1
        };
    }).filter(e => e.ms > 0).sort((a, b) => a.ms - b.ms);

    if (!events.length) {
        return {
            hasTilt: false,
            isTiltActive: () => false,
            isMovingActive: () => false
        };
    }

    const tiltIntervals = [];
    const movingIntervals = [];
    let moving = 0;

    const pushIv = (list, start, end) => {
        list.push({ start, end });
    };
    const closeOpen = (list, end) => {
        for (let i = list.length - 1; i >= 0; i--) {
            if (list[i].end === Number.POSITIVE_INFINITY) {
                list[i].end = end;
                return;
            }
        }
    };

    let hasTilt = false;
    for (const e of events) {
        if (e.type === 'moving') {
            if (e.state === 1 && moving === 0) {
                moving = 1;
                pushIv(movingIntervals, e.ms, Number.POSITIVE_INFINITY);
            } else if (e.state === 0 && moving === 1) {
                moving = 0;
                closeOpen(movingIntervals, e.ms);
            }
        } else if (e.type === 'tilt' || e.type === 'tilt_alarm') {
            hasTilt = true;
            if (e.state === 1 || e.type === 'tilt_alarm') {
                pushIv(tiltIntervals, e.ms, e.ms + MOTION_HOLD_MS);
            }
        }
    }

    const merge = (list) => {
        list.sort((a, b) => a.start - b.start);
        const out = [];
        for (const iv of list) {
            if (!out.length || iv.start > out[out.length - 1].end) {
                out.push({ start: iv.start, end: iv.end });
            } else {
                out[out.length - 1].end = Math.max(out[out.length - 1].end, iv.end);
            }
        }
        return out;
    };

    const tiltMerged = merge(tiltIntervals);
    const movingMerged = merge(movingIntervals);

    const inIntervals = (merged, ms) => {
        for (let i = 0; i < merged.length; i++) {
            if (ms < merged[i].start) return false;
            if (ms <= merged[i].end) return true;
        }
        return false;
    };

    return {
        hasTilt,
        isTiltActive: (ms) => inIntervals(tiltMerged, ms),
        isMovingActive: (ms) => inIntervals(movingMerged, ms)
    };
}

function wifiAnchoredCentroid(cluster) {
    const wifi = cluster.filter(isWifi);
    const src = wifi.length ? wifi : cluster;
    let lat = 0;
    let lng = 0;
    for (const p of src) {
        lat += Number(p.latitude);
        lng += Number(p.longitude);
    }
    const n = src.length || 1;
    // Prefer the last WiFi fix's identity fields when available (more stable pin).
    const prefer = wifi.length ? wifi[wifi.length - 1] : cluster[cluster.length - 1];
    return {
        latitude: lat / n,
        longitude: lng / n,
        direction: prefer.direction,
        speed: 0,
        locationlogid: prefer.locationlogid,
        eventtime: prefer.eventtime,
        _stop: true,
        _clusterSize: cluster.length,
        _wifiAnchored: wifi.length > 0
    };
}

/**
 * @param {Array} rawPoints oldest -> newest
 * @param {Array} motions from fngetsecuritytrackhistory
 * @returns {{ points: Array, stops: Array, stats: Object }}
 */
export function buildCleanedTrack(rawPoints, motions) {
    const points = (rawPoints || []).filter(p =>
        p && p.latitude != null && p.longitude != null &&
        Number(p.latitude) !== 0 && Number(p.longitude) !== 0
    );
    if (points.length === 0) {
        return { points: [], stops: [], stats: { raw: 0, cleaned: 0, stops: 0 } };
    }

    for (const p of points) p._ms = pointTimeMs(p);

    const hasSensorMotions = Array.isArray(motions) && motions.length > 0;
    const { hasTilt, isTiltActive, isMovingActive } = buildMotionFns(motions);
    const cleaned = [];
    const stops = [];

    let cluster = [points[0]];
    let inStop = true;
    let leaveStreak = 0;

    const flushStop = () => {
        if (!cluster.length) return;
        const anchor = wifiAnchoredCentroid(cluster);
        const first = cluster[0];
        const last = cluster[cluster.length - 1];

        cleaned.push({
            ...anchor,
            eventtime: first.eventtime,
            _ms: first._ms,
            speed: 0
        });
        if (last._ms !== first._ms) {
            cleaned.push({
                ...anchor,
                eventtime: last.eventtime,
                _ms: last._ms,
                locationlogid: last.locationlogid,
                speed: 0
            });
        }

        stops.push({
            latitude: anchor.latitude,
            longitude: anchor.longitude,
            // Visual radius stays tight; don't grow the circle to GPS outliers.
            radius: DISPLAY_STOP_RADIUS_M,
            startMs: first._ms,
            endMs: last._ms,
            count: cluster.length,
            wifiAnchored: anchor._wifiAnchored
        });
        cluster = [];
        leaveStreak = 0;
    };

    for (let i = 1; i < points.length; i++) {
        const p = points[i];
        const tiltMotion = isTiltActive(p._ms);
        const gpsMoving = isMovingActive(p._ms);
        const speedMotion = Number(p.speed) >= SPEED_MOTION_KMH;

        if (inStop) {
            const anchor = wifiAnchoredCentroid(cluster.length ? cluster : [p]);
            const dist = haversineMeters(anchor, p);

            // Strong leave: tilt + displacement (preferred when tilt data exists).
            // Weak leave: no tilt channel available — need displacement, and either
            // GPSPRO moving or speed. Hard leave: very large jump with speed.
            let leaveCandidate = false;
            if (hasTilt) {
                leaveCandidate = (tiltMotion && dist >= EXIT_MOVE_M) ||
                    (speedMotion && dist >= HARD_EXIT_M);
            } else if (hasSensorMotions) {
                // Have GPSPRO moving but no tilt events in range — still require
                // large displacement; don't trust moving/speed alone near the stop.
                leaveCandidate = ((gpsMoving || speedMotion) && dist >= EXIT_MOVE_M) ||
                    (speedMotion && dist >= HARD_EXIT_M);
            } else {
                // No sensor payload: displacement-only with a higher bar.
                leaveCandidate = dist >= EXIT_MOVE_M && (speedMotion || dist >= HARD_EXIT_M);
            }

            if (leaveCandidate) {
                leaveStreak += 1;
            } else {
                leaveStreak = 0;
            }

            if (leaveStreak >= EXIT_CONFIRM_POINTS) {
                flushStop();
                inStop = false;
                cleaned.push({ ...p });
                leaveStreak = 0;
                continue;
            }
            cluster.push(p);
            continue;
        }

        // Moving mode: spaced vertices only (WiFi no longer bypasses spacing —
        // WiFi jumps were drawing purple spider legs out of stops).
        const prev = cleaned[cleaned.length - 1] || p;
        const step = haversineMeters(prev, p);
        if (step >= MIN_VERTEX_M || i === points.length - 1) {
            cleaned.push({ ...p });
        }

        // Re-enter stop when quiet again near the current position.
        const quiet = !tiltMotion && !(speedMotion && step >= MIN_VERTEX_M);
        if (quiet && step < STOP_RADIUS_M) {
            inStop = true;
            cluster = [p];
            leaveStreak = 0;
            if (cleaned.length && cleaned[cleaned.length - 1].locationlogid === p.locationlogid) {
                cleaned.pop();
            }
        }
    }

    if (inStop && cluster.length) {
        flushStop();
    } else if (!cleaned.length) {
        cleaned.push({ ...points[0] });
        if (points.length > 1) cleaned.push({ ...points[points.length - 1] });
    }

    // Merge consecutive stop bookends that land on nearly the same place
    // (prevents stacked stop circles from brief false departures).
    const mergedStops = [];
    for (const s of stops) {
        const prev = mergedStops[mergedStops.length - 1];
        if (prev && haversineMeters(prev, s) < STOP_RADIUS_M) {
            prev.endMs = Math.max(prev.endMs, s.endMs);
            prev.count += s.count;
            prev.wifiAnchored = prev.wifiAnchored || s.wifiAnchored;
        } else {
            mergedStops.push({ ...s });
        }
    }

    const outPoints = cleaned.map(p => {
        const { _ms, ...rest } = p;
        return rest;
    });

    return {
        points: outPoints,
        stops: mergedStops,
        stats: {
            raw: points.length,
            cleaned: outPoints.length,
            stops: mergedStops.length,
            reduced: points.length > 0
                ? Math.round((1 - outPoints.length / points.length) * 100)
                : 0
        }
    };
}
